import React, { useState } from 'react';
import { Search, Filter, X, CalendarRange } from 'lucide-react';
import { FiltrosGlobais, initialFiltros } from '../types';

interface Props {
  filtros: FiltrosGlobais;
  setFiltros: React.Dispatch<React.SetStateAction<FiltrosGlobais>>;
  cidades: string[];
}

// Formata data local como yyyy-mm-dd (sem toISOString: UTC deslocaria −1 dia à noite)
const toISODateLocal = (d: Date) => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${dia}`;
};

const toISOMonthLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const PRESETS_VENCIMENTO = [
  { value: 'todos', label: 'Todo o período' },
  { value: 'mes_atual', label: 'Mês atual' },
  { value: 'mes_passado', label: 'Mês passado' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '60', label: 'Últimos 60 dias' },
  { value: '90', label: 'Últimos 90 dias' },
  { value: 'ano', label: 'Este ano' },
  { value: 'custom', label: 'Personalizado' },
];

const PRESETS_COMPETENCIA = [
  { value: 'todos', label: 'Todas' },
  { value: 'mes_atual', label: 'Mês atual' },
  { value: 'mes_passado', label: 'Mês passado' },
  { value: '3m', label: 'Últimos 3 meses' },
  { value: '6m', label: 'Últimos 6 meses' },
  { value: 'ano', label: 'Este ano' },
  { value: 'custom', label: 'Personalizado' },
];

const GlobalFilters: React.FC<Props> = ({ filtros, setFiltros, cidades }) => {
  const [presetPeriodo, setPresetPeriodo] = useState('todos');

  const handleChange = (k: keyof FiltrosGlobais, v: string) => {
    setFiltros(prev => ({ ...prev, [k]: v }));
  };

  const handleDataManual = (k: 'dataInicio' | 'dataFim', v: string) => {
    setPresetPeriodo(v || filtros[k === 'dataInicio' ? 'dataFim' : 'dataInicio'] ? 'custom' : 'todos');
    handleChange(k, v);
  };

  const aplicarPreset = (preset: string, modo: FiltrosGlobais['periodoModo'] = filtros.periodoModo) => {
    setPresetPeriodo(preset);
    if (preset === 'custom') return;
    const hoje = new Date();
    let inicio = '';
    let fim = '';
    if (modo === 'competencia') {
      if (preset === 'mes_atual') {
        inicio = fim = toISOMonthLocal(hoje);
      } else if (preset === 'mes_passado') {
        inicio = fim = toISOMonthLocal(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));
      } else if (preset === '3m' || preset === '6m') {
        const meses = preset === '3m' ? 3 : 6;
        inicio = toISOMonthLocal(new Date(hoje.getFullYear(), hoje.getMonth() - (meses - 1), 1));
        fim = toISOMonthLocal(hoje);
      } else if (preset === 'ano') {
        inicio = `${hoje.getFullYear()}-01`;
        fim = `${hoje.getFullYear()}-12`;
      }
    } else {
      if (preset === 'mes_atual') {
        inicio = toISODateLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
        fim = toISODateLocal(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0));
      } else if (preset === 'mes_passado') {
        inicio = toISODateLocal(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));
        fim = toISODateLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 0));
      } else if (preset === '30' || preset === '60' || preset === '90') {
        const d = new Date(hoje);
        d.setDate(d.getDate() - Number(preset));
        inicio = toISODateLocal(d);
        fim = toISODateLocal(hoje);
      } else if (preset === 'ano') {
        inicio = toISODateLocal(new Date(hoje.getFullYear(), 0, 1));
        fim = toISODateLocal(new Date(hoje.getFullYear(), 11, 31));
      }
    }
    setFiltros(prev => ({ ...prev, periodoModo: modo, dataInicio: inicio, dataFim: fim }));
  };

  const trocarModo = (modo: FiltrosGlobais['periodoModo']) => {
    // Datas têm formato diferente por modo (yyyy-mm-dd × yyyy-mm): re-aplica o
    // preset ativo no modo novo; range personalizado não converte, então limpa.
    const presetsValidos = (modo === 'competencia' ? PRESETS_COMPETENCIA : PRESETS_VENCIMENTO).map(p => p.value);
    if (presetPeriodo !== 'custom' && presetsValidos.includes(presetPeriodo)) {
      aplicarPreset(presetPeriodo, modo);
    } else {
      setPresetPeriodo('custom');
      setFiltros(prev => ({ ...prev, periodoModo: modo, dataInicio: '', dataFim: '' }));
    }
  };

  const clearFilters = () => {
    setPresetPeriodo('todos');
    setFiltros({ ...initialFiltros });
  };

  const { dataInicio, dataFim, periodoModo, ...outrosFiltros } = filtros;
  const countActive =
    Object.values(outrosFiltros).filter(v => v !== 'Todas' && v !== 'Todos' && v !== '').length +
    (dataInicio || dataFim ? 1 : 0);

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-3 flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2 text-gray-500 font-semibold text-xs border-r pr-4">
        <Filter size={14} />
        Filtros Globais
        {countActive > 0 && (
          <span className="bg-[#5F6C37] text-white px-1.5 rounded-full text-[10px]">{countActive}</span>
        )}
      </div>

      <div className="relative flex-1 min-w-[200px] max-w-xs">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search size={14} className="text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-[#5F6C37] focus:border-[#5F6C37]"
          placeholder="Buscar cliente ou UC..."
          value={filtros.busca}
          onChange={(e) => handleChange('busca', e.target.value)}
        />
      </div>

      <select
        className="border border-gray-300 rounded-md text-xs py-1.5 pl-3 pr-8 focus:ring-[#5F6C37] focus:border-[#5F6C37]"
        value={filtros.cidade}
        onChange={(e) => handleChange('cidade', e.target.value)}
      >
        <option value="Todas">Todas as Cidades</option>
        {cidades.map(c => <option key={c} value={c}>{c}</option>)}
      </select>

      <select
        className="border border-gray-300 rounded-md text-xs py-1.5 pl-3 pr-8 focus:ring-[#5F6C37] focus:border-[#5F6C37]"
        value={filtros.risco}
        onChange={(e) => handleChange('risco', e.target.value)}
      >
        <option value="Todos">Todos os Riscos</option>
        <option value="RISCO ESTRATÉGICO ALTO">Estratégico Alto</option>
        <option value="RISCO ALTO">Risco Alto</option>
        <option value="RISCO MÉDIO">Risco Médio</option>
        <option value="RISCO BAIXO">Risco Baixo</option>
      </select>

      <select
        className="border border-gray-300 rounded-md text-xs py-1.5 pl-3 pr-8 focus:ring-[#5F6C37] focus:border-[#5F6C37]"
        value={filtros.score}
        onChange={(e) => handleChange('score', e.target.value)}
      >
        <option value="Todos">Todos os Scores</option>
        <option value="A">Score A (Até 15 dias)</option>
        <option value="B">Score B (Até 30 dias)</option>
        <option value="C">Score C (Até 60 dias)</option>
        <option value="D">Score D (Até 90 dias)</option>
        <option value="E">Score E (Mais 90 dias)</option>
      </select>

      <select
        className="border border-gray-300 rounded-md text-xs py-1.5 pl-3 pr-8 focus:ring-[#5F6C37] focus:border-[#5F6C37]"
        value={filtros.saldoAcumulado}
        onChange={(e) => handleChange('saldoAcumulado', e.target.value)}
      >
        <option value="Todos">Saldo: Todos</option>
        <option value="Com Saldo">Com Saldo Retido</option>
        <option value="Sem Saldo">Sem Saldo</option>
      </select>

      <div
        className={`flex items-center gap-1.5 border rounded-md pl-2.5 pr-1.5 py-0.5 bg-white ${
          filtros.dataInicio || filtros.dataFim ? 'border-[#5F6C37] ring-1 ring-[#5F6C37]/30' : 'border-gray-300'
        }`}
        title="Recorte por data de vencimento do boleto ou por mês da fatura (competência)"
      >
        <CalendarRange size={14} className={filtros.dataInicio || filtros.dataFim ? 'text-[#5F6C37]' : 'text-gray-400'} />
        <select
          className="border-0 text-xs py-1 pl-1 pr-5 focus:ring-0 text-gray-500 font-semibold bg-transparent"
          value={filtros.periodoModo}
          onChange={(e) => trocarModo(e.target.value as FiltrosGlobais['periodoModo'])}
        >
          <option value="vencimento">Vencimento</option>
          <option value="competencia">Mês da fatura</option>
        </select>
        <select
          className="border-0 text-xs py-1 pl-1 pr-6 focus:ring-0 text-gray-700 font-medium bg-transparent"
          value={presetPeriodo}
          onChange={(e) => aplicarPreset(e.target.value)}
        >
          {(filtros.periodoModo === 'competencia' ? PRESETS_COMPETENCIA : PRESETS_VENCIMENTO).map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
        {presetPeriodo !== 'todos' && (
          <>
            <input
              type={filtros.periodoModo === 'competencia' ? 'month' : 'date'}
              className="border border-gray-200 rounded text-xs py-0.5 px-1.5 text-gray-700 focus:ring-[#5F6C37] focus:border-[#5F6C37]"
              value={filtros.dataInicio}
              max={filtros.dataFim || undefined}
              onChange={(e) => handleDataManual('dataInicio', e.target.value)}
            />
            <span className="text-xs text-gray-400">até</span>
            <input
              type={filtros.periodoModo === 'competencia' ? 'month' : 'date'}
              className="border border-gray-200 rounded text-xs py-0.5 px-1.5 text-gray-700 focus:ring-[#5F6C37] focus:border-[#5F6C37]"
              value={filtros.dataFim}
              min={filtros.dataInicio || undefined}
              onChange={(e) => handleDataManual('dataFim', e.target.value)}
            />
          </>
        )}
      </div>

      {countActive > 0 && (
        <button
          onClick={clearFilters}
          className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium px-2 py-1 rounded-md hover:bg-red-50"
        >
          <X size={14} /> Limpar
        </button>
      )}
    </div>
  );
};

export default GlobalFilters;
